<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import learningApiUser from '@src/services/api/learning/user'
import DefineValidation from '@services/validation/DefineValidation'
import Modal from '@components/modal/Modal.vue'
import { useLearningAuthStore } from '@stores/auth'
import { commonUtil } from '@utils'

const router = useRouter()
const showCompleteModal = ref(false)
const showExpiredModal = ref(false)
const { tempAccessToken } = storeToRefs(useLearningAuthStore())
const errorMessage = ref('')

const { errors, meta, handleSubmit, handleReset, models } = new DefineValidation((modules) => {
  return {
    pincode: modules.string().required().label('認証コード'),
    newPassword: modules.string().required().min(8).password().label('新パスワード'),
    newPasswordConfirm: modules.string().required().oneOf([modules.ref('newPassword'), null], '入力した新しいログインパスワードと一致しません。')
  }
})

const isPincodeError = ref(false)

const formItems = [
  {
    id: 'pincode',
    type: 'text',
    label: '認証コード',
    length: 6,
    placeholder: '6桁のコード'
  },
  {
    id: 'newPassword',
    type: 'password',
    label: '新パスワード',
    length: 32,
    placeholder: ''
  },
  {
    id: 'newPasswordConfirm',
    type: 'password',
    label: '新パスワード（確認）',
    length: 32,
    placeholder: ''
  }
]

const toTop = () => {
  showCompleteModal.value = false
  showExpiredModal.value = false
  router.push({
    name: 'Login'
  })
}

const sendButtonDisabled = computed(() => {
  return !(meta.value.valid && meta.value.dirty)
})

const sendForm = handleSubmit(async (values) => {
  // 先に仮発行されたJWTトークンの有効期限を確認、切れていたらエラーモーダルを表示
  if (!commonUtil.checkTokenExpire(tempAccessToken.value)) {
    showExpiredModal.value = true
    return false
  }

  // 有効期限が切れていなかったらAPIを叩く
  const body = {
    pincode: models.pincode.value,
    password: models.newPassword.value,
    headers: {
      tempAuthorization: tempAccessToken.value
    }
  }
  learningApiUser.putReminder(body).then((res) => {
    showCompleteModal.value = true
  }).catch((err) => {
    console.error('patchUserSingup err', err)
    if (err.response.data.message === 'Unprocessable Entity') {
      errorMessage.value = '入力内容に問題があります。'
    } else if (err.response.status === 401) {
      errorMessage.value = '認証に失敗しました。認証コードを確認してください。'
    } else {
      errorMessage.value = 'エラーが発生しました。'
    }
    isPincodeError.value = true
  })
})
</script>

<template>
  <section>
    <div class="container">
      <h2>パスワード再設定（認証コード入力）</h2>
      <p>メールに記載された6桁の数字を入力し、新しいパスワードを入力して、変更ボタンを押してください。</p>
      <form @submit.prevent="sendForm">
        <div class="c-compBox">
          <div
            v-for="item in formItems"
            :key="item.id"
            class="p-form-field"
          >
            <label
              :for="item.id"
              class="c-input-label"
            ><span class="c-form-required">必須</span>
              {{ item.label }}</label>
            <input
              :id="item.id"
              v-model="models[item.id].value"
              :type="item.type"
              :class="`c-input-${item.type}`"
              :maxlength="item.length"
              :placeholder="item.placeholder"
              autocomplete="off"
            >
            <span
              v-if="errors.pincode"
              class="c-validationError"
            >{{ errors[item.id] }}</span>
            <span
              v-if="errors.newPassword"
              class="c-validationError"
            >{{ errors[item.id] }}</span>
            <span
              v-else-if="errors.newPasswordConfirm"
              class="c-validationError"
            >{{ errors[item.id] }}</span>
          </div>

          <!-- APIエラー表示 -->
          <div
            v-if="errorMessage"
            class="p-form-error">
            {{ errorMessage }}
          </div>

          <div class="authEmailSend__send">
            <button
              type="submit"
              class="c-button c-button--medium c-button--yellow"
            >
              変更
            </button>
            <RouterLink
              :to="{name:'Login'}"
              class="c-button c-button--medium c-button--border"
            >
              戻る
            </RouterLink>
          </div>
        </div>
      </form>
    </div>

    <Modal :show="showCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            パスワードを再設定しました。<br>ログインし直してください。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showExpiredModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            認証コードの期限が切れました。<br>最初からやり直してください。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </section>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }
}
.btnContainer {
  margin-top: 20px;
}
</style>
